import React, { useEffect, useState } from 'react';
import { ActivityIndicator, StyleSheet, View } from 'react-native';

import { AppBottomSheet, SearchableSelect, SheetAction } from '~/components/core';
import AIStrategyBuilderService from '~/api-services/AIStrategyBuilderService';
import { Template } from '~/api-services/AIStrategyBuilderService/types';
import { useStyles } from '~/hooks';
import { scaleSize } from '~/constants/scale';
import { Theme } from '~/theme';

type Props = {
    visible: boolean;
    onClose: () => void;
    // Start the wizard: blank (no uuid) or seeded from the chosen template.
    onChoose: (templateUuid?: string) => void;
};

// "＋" chooser: start from scratch, or pick one of the backend's fine-tuned templates from a
// searchable dropdown (the template catalog is expected to grow well beyond a few entries).
export default function CreateChooserSheet({ visible, onClose, onChoose }: Props) {
    const styles = useStyles(stylesGenerator);
    const [templates, setTemplates] = useState<Template[] | null>(null);
    const [templatePickerOpen, setTemplatePickerOpen] = useState(false);

    useEffect(() => {
        if (!visible || templates !== null) {
            return;
        }
        AIStrategyBuilderService.getTemplates()
            .then(({ data }) => {
                setTemplates(data?.status && data.returnData ? data.returnData : []);
            })
            .catch(() => setTemplates([]));
    }, [visible, templates]);

    return (
        <>
            <AppBottomSheet visible={visible && !templatePickerOpen} onClose={onClose} title="ai-strategy-new">
                <SheetAction label="ai-strategy-start-blank" onPress={() => onChoose()} />
                {templates === null ? (
                    <View style={styles.loadingRow}>
                        <ActivityIndicator />
                    </View>
                ) : templates.length > 0 ? (
                    <SheetAction label="ai-strategy-use-template" onPress={() => setTemplatePickerOpen(true)} />
                ) : null}
            </AppBottomSheet>

            <SearchableSelect
                visible={templatePickerOpen}
                onClose={() => setTemplatePickerOpen(false)}
                title="ai-strategy-use-template"
                options={(templates ?? []).map((t) => ({ value: t.uuid, label: t.name }))}
                onSelect={(uuid) => {
                    setTemplatePickerOpen(false);
                    onChoose(uuid);
                }}
            />
        </>
    );
}

const stylesGenerator = (_theme: Theme) =>
    StyleSheet.create({
        loadingRow: {
            paddingVertical: scaleSize(16),
            alignItems: 'center',
        },
    });
